import { useEffect, useState, Fragment } from "react";
import { api } from "@/lib/api";
import { useAuth, authHeaders, apiErrorMessage } from "@/lib/auth";
import { AdminLogin } from "@/pages/AdminLogin";
import { AdminChangePassword } from "@/pages/AdminChangePassword";
import { ConversationsTab, AllEnquiriesTab } from "@/pages/admin/AdminInboxTabs";
import { LogOut, Search, ChevronDown, ChevronUp, AlertCircle, Mail, Check, X, Circle, KeyRound, Inbox, MessageCircle, ListChecks } from "lucide-react";
import { cn } from "@/lib/utils";

const RowGroup = Fragment;
const SOURCE_LABELS = {
  contact_submissions: "Contact form",
  tool_pdpl: "PDPL Readiness",
  tool_iso27001: "ISO 27001",
  tool_software_cost: "Software Cost",
  tool_ai_opportunity: "AI Opportunity",
  careers: "Careers",
  "careers-application": "Job application",
};

function prettifySource(s) {
  if (SOURCE_LABELS[s]) return SOURCE_LABELS[s];
  if (s?.startsWith("download-")) return "Download · " + s.replace("download-", "").replace(/-/g, " ");
  return s || "—";
}

function EmailStatusPill({ status }) {
  const map = {
    sent: { icon: Check, label: "Sent", cls: "bg-wafa-primary/10 text-wafa-primary" },
    failed: { icon: X, label: "Failed", cls: "bg-red-100 text-red-700" },
    not_configured: { icon: Circle, label: "Not configured", cls: "bg-wafa-ink/10 text-wafa-ink/70" },
    pending: { icon: Mail, label: "Pending", cls: "bg-wafa-sunshine/40 text-wafa-deep" },
  };
  const entry = map[status] || map.pending;
  const Icon = entry.icon;
  return (
    <span data-testid={`email-status-${status || "pending"}`} className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", entry.cls)}>
      <Icon className="h-3 w-3" /> {entry.label}
    </span>
  );
}

export default function AdminLeads() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("enquiries");
  const [showChangePw, setShowChangePw] = useState(false);
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  const refreshEmailStatus = async () => {
    try { const { data } = await api.get("/admin/email-status", authHeaders()); setEmailStatus(data); } catch (_) {}
  };
  const fetchLeads = async () => {
    setLoading(true); setError("");
    try {
      const [leadsRes, summaryRes] = await Promise.all([
        api.get("/admin/leads?limit=500", authHeaders()),
        api.get("/admin/leads/summary", authHeaders()),
      ]);
      setLeads(leadsRes.data.items);
      setCounts(summaryRes.data.counts);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); }
      else if (err?.response?.status === 403) { setError("Your account does not have access to leads."); }
      else { setError(apiErrorMessage(err, "Failed to load leads.")); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user && !user.must_change_password && user.role === "admin") {
      fetchLeads(); refreshEmailStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  if (!user) return <AdminLogin title="Leads — Sign in" subtitle="Admin account required." />;
  if (user.must_change_password) return <AdminChangePassword forced onDone={() => window.location.reload()} />;
  if (showChangePw) return <AdminChangePassword onDone={() => setShowChangePw(false)} />;
  if (user.role !== "admin") {
    return (
      <section className="min-h-screen bg-wafa-cream/30" data-testid="admin-leads-forbidden">
        <div className="mx-auto max-w-md px-4 py-24 lg:px-6">
          <div className="rounded-lg border border-red-200 bg-white p-8">
            <h1 className="font-heading text-2xl font-semibold text-wafa-deep">Not authorized</h1>
            <p className="mt-2 text-sm text-wafa-ink/70">Leads dashboard is admin-only. Your role is <span className="font-mono">{user.role}</span>.</p>
            <button onClick={logout} className="mt-6 inline-flex items-center gap-2 rounded-md bg-wafa-primary px-4 py-2 text-sm font-semibold text-white hover:bg-wafa-deep" data-testid="admin-forbidden-logout">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </section>
    );
  }

  const sendTestEmail = async () => {
    setTesting(true); setTestResult(null);
    try { const { data } = await api.post("/admin/test-email", {}, authHeaders()); setTestResult(data); }
    catch (_) { setTestResult({ ok: false, message: "Test failed. See server logs." }); }
    finally { setTesting(false); refreshEmailStatus(); }
  };

  const filtered = leads.filter((l) => {
    if (filter !== "all" && l.source !== filter) return false;
    if (!search.trim()) return true;
    return JSON.stringify(l).toLowerCase().includes(search.trim().toLowerCase());
  });

  const knownSources = Object.keys(SOURCE_LABELS);
  const dynamicSources = Object.keys(counts).filter((k) => !knownSources.includes(k));

  return (
    <section className="bg-wafa-cream/30" data-testid="admin-leads-page">
      <div className="mx-auto max-w-7xl px-4 py-12 lg:px-6 lg:py-16">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-wafa-deep sm:text-4xl">Leads</h1>
            <p className="mt-1 text-sm text-wafa-ink/60">Signed in as <span className="font-mono">{user.email}</span></p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={sendTestEmail} disabled={testing} data-testid="admin-test-email" className="inline-flex items-center gap-2 rounded-md border border-wafa-deep/15 bg-white px-3 py-2 text-sm text-wafa-deep hover:bg-wafa-cream/40 disabled:opacity-60">
              <Mail className="h-4 w-4" /> {testing ? "Sending…" : "Send test email"}
            </button>
            <button onClick={() => setShowChangePw(true)} className="inline-flex items-center gap-2 rounded-md border border-wafa-deep/15 bg-white px-3 py-2 text-sm hover:bg-wafa-cream/40" data-testid="admin-change-pw">
              <KeyRound className="h-4 w-4" /> Change password
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-md border border-wafa-deep/15 px-3 py-2 text-sm text-wafa-deep hover:bg-white">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>

        {emailStatus ? (
          <div data-testid="admin-email-panel" className="mt-6 grid gap-4 rounded-lg border border-wafa-deep/10 bg-white p-5 sm:grid-cols-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-wafa-ink/60">Email sending</div>
              <div className="mt-1">
                {emailStatus.configured ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-wafa-primary/10 px-2 py-0.5 text-xs font-semibold text-wafa-primary" data-testid="email-panel-configured"><Check className="h-3 w-3" /> Configured</span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-wafa-ink/10 px-2 py-0.5 text-xs font-semibold text-wafa-ink/70" data-testid="email-panel-not-configured"><Circle className="h-3 w-3" /> Not configured</span>
                )}
              </div>
              <div className="mt-2 text-xs text-wafa-ink/70">Sends to <span className="font-mono">{emailStatus.to}</span></div>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-wafa-ink/60">Method in use</div>
              <div className="mt-1 font-mono text-sm text-wafa-deep">{emailStatus.transport?.name || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-wafa-ink/60">Last send attempt</div>
              {emailStatus.last_attempt ? (
                <>
                  <div className="mt-1 text-sm text-wafa-deep">{emailStatus.last_attempt.ok ? "Sent" : "Failed"} · <span className="text-xs text-wafa-ink/60">{emailStatus.last_attempt.kind}</span></div>
                  <div className="mt-1 text-xs text-wafa-ink/70">{emailStatus.last_attempt.message}</div>
                </>
              ) : <div className="mt-1 text-xs text-wafa-ink/60">No attempt recorded yet.</div>}
            </div>
          </div>
        ) : null}

        <div className="mt-6 flex gap-1 rounded-md bg-white p-1 sm:w-fit">
          <TabBtn active={tab === "enquiries"} onClick={() => setTab("enquiries")} testid="tab-enquiries" icon={Inbox}>All Enquiries</TabBtn>
          <TabBtn active={tab === "conversations"} onClick={() => setTab("conversations")} testid="tab-conversations" icon={MessageCircle}>Conversations</TabBtn>
          <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} testid="tab-leads" icon={ListChecks}>Leads (legacy)</TabBtn>
        </div>

        {tab === "enquiries" ? <AllEnquiriesTab role={user.role} /> : null}
        {tab === "conversations" ? <ConversationsTab /> : null}
        {tab !== "leads" ? null : (
          <>
        {testResult ? (
          <div data-testid="admin-test-email-result" className={cn("mt-4 rounded-md border p-3 text-sm", testResult.ok ? "border-wafa-primary/30 bg-wafa-primary/5" : "border-red-200 bg-red-50 text-red-800")}>
            <div className="font-semibold">{testResult.ok ? "Test email sent" : "Test email did not send"}</div>
            {testResult.message ? <div className="mt-1 text-xs text-wafa-ink/70">{testResult.message}</div> : null}
          </div>
        ) : null}

        <div className="mt-8 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <button onClick={() => setFilter("all")} data-testid="admin-filter-all" className={cn("rounded-lg border p-4 text-left transition-colors", filter === "all" ? "border-wafa-primary bg-white" : "border-wafa-deep/10 bg-white/70 hover:bg-white")}>
            <div className="text-xs uppercase tracking-wider text-wafa-ink/60">All</div>
            <div className="mt-1 font-heading text-2xl font-semibold text-wafa-deep">{leads.length}</div>
          </button>
          {knownSources.map((key) => (
            <button key={key} onClick={() => setFilter(key)} data-testid={`admin-filter-${key}`} className={cn("rounded-lg border p-4 text-left transition-colors", filter === key ? "border-wafa-primary bg-white" : "border-wafa-deep/10 bg-white/70 hover:bg-white")}>
              <div className="text-xs uppercase tracking-wider text-wafa-ink/60">{SOURCE_LABELS[key]}</div>
              <div className="mt-1 font-heading text-2xl font-semibold text-wafa-deep">{counts[key] || 0}</div>
            </button>
          ))}
          {dynamicSources.map((key) => (
            <button key={key} onClick={() => setFilter(key)} data-testid={`admin-filter-${key}`} className={cn("rounded-lg border p-4 text-left transition-colors", filter === key ? "border-wafa-primary bg-white" : "border-wafa-deep/10 bg-white/70 hover:bg-white")}>
              <div className="text-xs uppercase tracking-wider text-wafa-ink/60">{prettifySource(key)}</div>
              <div className="mt-1 font-heading text-2xl font-semibold text-wafa-deep">{counts[key] || 0}</div>
            </button>
          ))}
        </div>

        <div className="mt-8 flex items-center gap-3 rounded-md border border-wafa-deep/10 bg-white px-3">
          <Search className="h-4 w-4 text-wafa-ink/50" />
          <input placeholder="Search leads…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-search" className="h-11 flex-1 bg-transparent text-sm focus:outline-none" />
        </div>

        {error ? <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="admin-error"><AlertCircle className="mr-1 inline h-4 w-4" />{error}</div> : null}
        {loading ? <div className="mt-8 text-sm text-wafa-ink/60">Loading…</div> : null}

        <div className="mt-6 overflow-hidden rounded-lg border border-wafa-deep/10 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-wafa-cream/40 text-left text-xs uppercase tracking-wider text-wafa-deep">
              <tr><th className="px-4 py-3">Source</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Submitted</th><th className="px-4 py-3 text-right">Details</th></tr>
            </thead>
            <tbody className="divide-y divide-wafa-deep/10">
              {filtered.map((l, i) => {
                const name = l.first_name || l.name || "—";
                const last = l.last_name ? ` ${l.last_name}` : "";
                const email = l.email || "—";
                const company = l.company || (l.payload && l.payload.company) || "—";
                const isOpen = expanded === i;
                return (
                  <RowGroup key={l.id || `${l.source}-${i}`}>
                    <tr data-testid={`admin-row-${i}`} className="align-top">
                      <td className="px-4 py-3"><span className="inline-flex rounded-full bg-wafa-cream px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-wafa-deep">{prettifySource(l.source)}</span></td>
                      <td className="px-4 py-3"><div className="font-medium text-wafa-deep">{name}{last}</div><div className="text-xs text-wafa-ink/60">{email}</div></td>
                      <td className="px-4 py-3 text-wafa-ink/80">{company}</td>
                      <td className="px-4 py-3"><EmailStatusPill status={l.email_status} /></td>
                      <td className="px-4 py-3 text-xs text-wafa-ink/60">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right"><button onClick={() => setExpanded(isOpen ? null : i)} className="inline-flex items-center gap-1 text-xs font-medium text-wafa-deep" data-testid={`admin-toggle-${i}`}>{isOpen ? "Hide" : "View"} {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</button></td>
                    </tr>
                    {isOpen ? (
                      <tr><td colSpan={6} className="bg-wafa-cream/20 px-4 py-4"><pre className="max-w-full overflow-auto rounded bg-white p-3 text-[11px] leading-relaxed text-wafa-ink/80">{JSON.stringify(l, null, 2)}</pre></td></tr>
                    ) : null}
                  </RowGroup>
                );
              })}
              {!loading && filtered.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-wafa-ink/60">No leads yet.</td></tr> : null}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>
    </section>
  );
}

function TabBtn({ active, onClick, children, testid, icon: Icon }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors",
        active ? "bg-wafa-deep text-white" : "text-wafa-ink/70 hover:bg-wafa-cream/40",
      )}
    >
      {Icon ? <Icon className="h-4 w-4" /> : null} {children}
    </button>
  );
}
